import React from 'react';
import styled from '@emotion/styled';
import { SerializedStyles } from '@emotion/react';
import { AigleInput, AigleInputProps } from './AigleInput';
import { aigleColor } from '@styles/colorStyleGuide';

/**
 * AigleSearchInput Props
 */
export interface AigleSearchInputProps extends Omit<AigleInputProps, 'endAdornment' | 'type'> {
  /** 검색 아이콘 */
  searchIcon?: React.ReactNode;
  /** 검색 이벤트 핸들러 (엔터키 또는 아이콘 클릭) */
  onSearch?: (value: string) => void;
  /** 아이콘 클릭 활성화 여부 */
  clickableIcon?: boolean;
}

// Styled Components
const SearchIconWrapper = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  color: ${aigleColor.grey.dark};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: color 0.2s ease-in-out;
  
  ${({ $clickable }) => $clickable && `
    &:hover {
      color: ${aigleColor.grey.darkest};
    }
  `}
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

// 기본 검색 아이콘 (돋보기)
const DefaultSearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * AigleSearchInput Component
 * 
 * 검색을 위한 Input 컴포넌트
 * 검색 아이콘과 엔터키 검색 기능 포함
 * 
 * @example
 * // Basic Search Input
 * <AigleSearchInput 
 *   placeholder="검색..." 
 *   onSearch={(value) => console.log(value)}
 * />
 * 
 * @example
 * // Clickable Icon
 * <AigleSearchInput 
 *   placeholder="검색어를 입력하세요"
 *   clickableIcon
 *   onSearch={handleSearch}
 * />
 * 
 * @example
 * // With Label
 * <AigleSearchInput 
 *   label="제품 검색"
 *   placeholder="제품명을 입력하세요"
 *   onSearch={handleSearch}
 * />
 */
export const AigleSearchInput = React.forwardRef<HTMLInputElement, AigleSearchInputProps>(
  (
    {
      searchIcon = <DefaultSearchIcon />,
      onSearch,
      clickableIcon = false,
      value,
      onChange,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const handleIconClick = () => {
      if (clickableIcon && onSearch && value) {
        onSearch(String(value));
      }
    };
    
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onSearch && value) {
        e.preventDefault();
        onSearch(String(value));
      }
      onKeyDown?.(e);
    };
    
    const endAdornment = (
      <SearchIconWrapper
        $clickable={clickableIcon}
        onClick={handleIconClick}
        role={clickableIcon ? 'button' : undefined}
        aria-label={clickableIcon ? '검색' : undefined}
      >
        {searchIcon}
      </SearchIconWrapper>
    );
    
    return (
      <AigleInput
        ref={ref}
        type="search"
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        endAdornment={endAdornment}
        {...props}
      />
    );
  }
);

AigleSearchInput.displayName = 'AigleSearchInput';

export default AigleSearchInput;

